<script>
    import { onMount } from 'svelte';
    import { automationsAPI, chatbotAPI } from './api.js';

    let automations = [];
    let chatbots = [];
    let loading = false;
    let error = null;

    // Modal state
    let showModal = false;
    let editingAutomation = null;
    let formData = {
        name: '',
        description: '',
        trigger_type: 'NEW_MESSAGE',
        trigger_config: {},
        action_type: 'TRIGGER_CHATBOT',
        action_config: {},
        chatbot_config_id: null,
        is_active: true
    };

    // Trigger and Action types with descriptions
    const triggerTypes = [
        {
            value: 'NEW_CONVERSATION',
            label: 'Nueva Conversación',
            icon: '💬',
            description: 'Se activa cuando se crea una nueva conversación',
            configFields: []
        },
        {
            value: 'NEW_MESSAGE',
            label: 'Nuevo Mensaje',
            icon: '📩',
            description: 'Se activa cuando se recibe un nuevo mensaje',
            configFields: ['direction'] // incoming, outgoing, or both
        },
        {
            value: 'KEYWORD_MATCH',
            label: 'Coincidencia de Palabra Clave',
            icon: '🔑',
            description: 'Se activa cuando un mensaje contiene palabras clave específicas',
            configFields: ['keywords', 'match_type'] // exact, contains, regex
        },
        {
            value: 'TIME_BASED',
            label: 'Basado en Tiempo',
            icon: '⏰',
            description: 'Se activa en intervalos de tiempo específicos',
            configFields: ['schedule', 'timezone']
        },
        {
            value: 'CONTACT_CREATED',
            label: 'Contacto Creado',
            icon: '👤',
            description: 'Se activa cuando se crea un nuevo contacto',
            configFields: []
        },
        {
            value: 'LABEL_ADDED',
            label: 'Etiqueta Añadida',
            icon: '🏷️',
            description: 'Se activa cuando se añade una etiqueta específica',
            configFields: ['label_id']
        }
    ];

    const actionTypes = [
        {
            value: 'SEND_MESSAGE',
            label: 'Enviar Mensaje',
            icon: '📤',
            description: 'Envía un mensaje automático',
            configFields: ['message_template']
        },
        {
            value: 'ASSIGN_LABEL',
            label: 'Asignar Etiqueta',
            icon: '🏷️',
            description: 'Añade una etiqueta a la conversación',
            configFields: ['label_id']
        },
        {
            value: 'ASSIGN_AGENT',
            label: 'Asignar Agente',
            icon: '👨‍💼',
            description: 'Asigna la conversación a un agente específico',
            configFields: ['agent_id', 'assignment_logic'] // specific, round_robin, least_active
        },
        {
            value: 'CREATE_CONTACT',
            label: 'Crear Contacto',
            icon: '➕',
            description: 'Crea un nuevo contacto automáticamente',
            configFields: ['contact_data']
        },
        {
            value: 'SEND_EMAIL',
            label: 'Enviar Email',
            icon: '📧',
            description: 'Envía un email de notificación',
            configFields: ['email_to', 'email_subject', 'email_template']
        },
        {
            value: 'TRIGGER_CHATBOT',
            label: 'Activar Chatbot',
            icon: '🤖',
            description: 'Activa un chatbot en la conversación',
            configFields: ['chatbot_id']
        }
    ];

    // Temporary config state
    let triggerConfigState = {
        keywords: '',
        match_type: 'contains',
        direction: 'incoming',
        schedule: '',
        timezone: 'America/Mexico_City',
        label_id: ''
    };

    let actionConfigState = {
        message_template: '',
        label_id: '',
        agent_id: '',
        assignment_logic: 'round_robin',
        email_to: '',
        email_subject: '',
        email_template: '',
        chatbot_id: ''
    };

    onMount(() => {
        loadData();
    });

    async function loadData() {
        loading = true;
        error = null;
        try {
            const [automationsData, chatbotsData] = await Promise.all([
                automationsAPI.getAll(),
                chatbotAPI.getAll()
            ]);
            automations = automationsData;
            chatbots = chatbotsData;
        } catch (err) {
            error = err.message || 'Error al cargar datos';
            console.error('Error loading data:', err);
        } finally {
            loading = false;
        }
    }

    function openCreateModal() {
        editingAutomation = null;
        formData = {
            name: '',
            description: '',
            trigger_type: 'NEW_MESSAGE',
            trigger_config: {},
            action_type: 'TRIGGER_CHATBOT',
            action_config: {},
            chatbot_config_id: null,
            is_active: true
        };
        resetConfigStates();
        showModal = true;
    }

    function openEditModal(automation) {
        editingAutomation = automation;
        formData = {
            name: automation.name,
            description: automation.description || '',
            trigger_type: automation.trigger_type,
            trigger_config: automation.trigger_config || {},
            action_type: automation.action_type,
            action_config: automation.action_config || {},
            chatbot_config_id: automation.chatbot_config_id,
            is_active: automation.is_active
        };

        // Load config states
        loadConfigStates();
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        editingAutomation = null;
    }

    function resetConfigStates() {
        triggerConfigState = {
            keywords: '',
            match_type: 'contains',
            direction: 'incoming',
            schedule: '',
            timezone: 'America/Mexico_City',
            label_id: ''
        };
        actionConfigState = {
            message_template: '',
            label_id: '',
            agent_id: '',
            assignment_logic: 'round_robin',
            email_to: '',
            email_subject: '',
            email_template: '',
            chatbot_id: ''
        };
    }

    function loadConfigStates() {
        const tc = formData.trigger_config || {};
        const ac = formData.action_config || {};

        triggerConfigState = {
            keywords: tc.keywords || '',
            match_type: tc.match_type || 'contains',
            direction: tc.direction || 'incoming',
            schedule: tc.schedule || '',
            timezone: tc.timezone || 'America/Mexico_City',
            label_id: tc.label_id || ''
        };

        actionConfigState = {
            message_template: ac.message_template || '',
            label_id: ac.label_id || '',
            agent_id: ac.agent_id || '',
            assignment_logic: ac.assignment_logic || 'round_robin',
            email_to: ac.email_to || '',
            email_subject: ac.email_subject || '',
            email_template: ac.email_template || '',
            chatbot_id: ac.chatbot_id || ''
        };
    }

    function buildTriggerConfig() {
        const config = {};
        const trigger = triggerTypes.find(t => t.value === formData.trigger_type);

        if (trigger) {
            trigger.configFields.forEach(field => {
                if (triggerConfigState[field]) {
                    config[field] = triggerConfigState[field];
                }
            });
        }

        return config;
    }

    function buildActionConfig() {
        const config = {};
        const action = actionTypes.find(a => a.value === formData.action_type);

        if (action) {
            action.configFields.forEach(field => {
                if (actionConfigState[field]) {
                    config[field] = actionConfigState[field];
                }
            });
        }

        return config;
    }

    async function saveAutomation() {
        if (!formData.name) {
            alert('Por favor ingrese un nombre para la automatización');
            return;
        }

        loading = true;
        error = null;

        try {
            const automationData = {
                name: formData.name,
                description: formData.description,
                trigger_type: formData.trigger_type,
                trigger_config: buildTriggerConfig(),
                action_type: formData.action_type,
                action_config: buildActionConfig(),
                chatbot_config_id: formData.action_type === 'TRIGGER_CHATBOT' ? parseInt(actionConfigState.chatbot_id) : null,
                is_active: formData.is_active
            };

            if (editingAutomation) {
                await automationsAPI.update(editingAutomation.id, automationData);
            } else {
                await automationsAPI.create(automationData);
            }

            closeModal();
            await loadData();
        } catch (err) {
            error = err.message || 'Error al guardar la automatización';
            console.error('Error saving automation:', err);
        } finally {
            loading = false;
        }
    }

    async function toggleActive(automation) {
        loading = true;
        error = null;

        try {
            await automationsAPI.update(automation.id, { is_active: !automation.is_active });
            await loadData();
        } catch (err) {
            error = err.message || 'Error al actualizar la automatización';
            console.error('Error toggling automation:', err);
        } finally {
            loading = false;
        }
    }

    async function deleteAutomation(id) {
        if (!confirm('¿Está seguro de eliminar esta automatización?')) return;

        loading = true;
        error = null;

        try {
            await automationsAPI.delete(id);
            await loadData();
        } catch (err) {
            error = err.message || 'Error al eliminar la automatización';
            console.error('Error deleting automation:', err);
        } finally {
            loading = false;
        }
    }

    function getTriggerInfo(type) {
        return triggerTypes.find(t => t.value === type) || { label: type, icon: '⚡' };
    }

    function getActionInfo(type) {
        return actionTypes.find(a => a.value === type) || { label: type, icon: '🎯' };
    }

    function getCurrentTrigger() {
        return triggerTypes.find(t => t.value === formData.trigger_type);
    }

    function getCurrentAction() {
        return actionTypes.find(a => a.value === formData.action_type);
    }
</script>

<style>
    .automations-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .header h1 {
        margin: 0;
        color: #333;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-edit {
        background: #ff9800;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-edit:hover {
        background: #f57c00;
    }

    .btn-danger {
        background: #f44336;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-danger:hover {
        background: #d32f2f;
    }

    .btn-toggle {
        padding: 8px 16px;
        font-size: 13px;
    }

    .btn-toggle.active {
        background: #4caf50;
        color: white;
    }

    .btn-toggle.inactive {
        background: #757575;
        color: white;
    }

    .error-message {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
        border-left: 4px solid #c62828;
    }

    .automations-list {
        display: flex;
        flex-direction: column;
        gap: 15px;
        margin-top: 20px;
    }

    .automation-card {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s;
    }

    .automation-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    .automation-card.inactive {
        opacity: 0.6;
        background: #f5f5f5;
    }

    .automation-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 15px;
    }

    .automation-title-section {
        flex: 1;
    }

    .automation-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin: 0 0 8px 0;
    }

    .automation-description {
        color: #757575;
        font-size: 14px;
        margin: 0;
    }

    .status-badge {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .status-badge.active {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .status-badge.inactive {
        background: #eeeeee;
        color: #616161;
    }

    .automation-flow {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 15px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 6px;
    }

    .flow-box {
        flex: 1;
        padding: 15px;
        background: white;
        border: 2px solid #e0e0e0;
        border-radius: 6px;
        text-align: center;
    }

    .flow-box.trigger {
        border-color: #2196f3;
    }

    .flow-box.action {
        border-color: #4caf50;
    }

    .flow-icon {
        font-size: 32px;
        margin-bottom: 8px;
    }

    .flow-label {
        font-size: 11px;
        text-transform: uppercase;
        color: #757575;
        margin-bottom: 4px;
        font-weight: 600;
    }

    .flow-title {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin: 0;
    }

    .flow-arrow {
        font-size: 24px;
        color: #757575;
    }

    .automation-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 30px;
        width: 90%;
        max-width: 800px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal h2 {
        margin: 0 0 25px 0;
        color: #333;
    }

    .form-section {
        margin-bottom: 25px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 8px;
    }

    .form-section h3 {
        margin: 0 0 15px 0;
        color: #333;
        font-size: 16px;
    }

    .form-group {
        margin-bottom: 18px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group textarea {
        min-height: 80px;
        resize: vertical;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .checkbox-group label {
        margin: 0;
        cursor: pointer;
    }

    .type-selector {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 10px;
        margin-bottom: 20px;
    }

    .type-option {
        padding: 15px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        background: white;
        text-align: center;
    }

    .type-option:hover {
        border-color: #2196f3;
        background: #f5f9ff;
    }

    .type-option.selected {
        border-color: #2196f3;
        background: #e3f2fd;
    }

    .type-option-icon {
        font-size: 32px;
        margin-bottom: 8px;
    }

    .type-option-label {
        font-weight: 600;
        color: #333;
        font-size: 14px;
        margin-bottom: 4px;
    }

    .type-option-desc {
        font-size: 11px;
        color: #757575;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 25px;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #757575;
    }

    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #757575;
    }

    .empty-state h3 {
        color: #333;
        margin-bottom: 10px;
    }

    .info-box {
        background: #e3f2fd;
        border-left: 4px solid #2196f3;
        padding: 12px 16px;
        margin-bottom: 20px;
        border-radius: 4px;
    }

    .info-box p {
        margin: 0;
        color: #0d47a1;
        font-size: 13px;
    }
</style>

<div class="automations-container">
    <div class="header">
        <h1>⚡ Automatizaciones</h1>
        <button class="btn btn-primary" on:click={openCreateModal}>
            + Nueva Automatización
        </button>
    </div>

    {#if error}
        <div class="error-message">{error}</div>
    {/if}

    <div class="info-box">
        <p>Las automatizaciones te permiten crear flujos de trabajo automáticos. Define un disparador (trigger) y una acción que se ejecutará automáticamente.</p>
    </div>

    {#if loading}
        <div class="loading">Cargando...</div>
    {:else if automations.length === 0}
        <div class="empty-state">
            <h3>No hay automatizaciones configuradas</h3>
            <p>Crea tu primera automatización para comenzar a automatizar tareas repetitivas.</p>
        </div>
    {:else}
        <div class="automations-list">
            {#each automations as automation}
                <div class="automation-card" class:inactive={!automation.is_active}>
                    <div class="automation-header">
                        <div class="automation-title-section">
                            <h3 class="automation-title">{automation.name}</h3>
                            {#if automation.description}
                                <p class="automation-description">{automation.description}</p>
                            {/if}
                        </div>
                        <span class="status-badge" class:active={automation.is_active} class:inactive={!automation.is_active}>
                            {automation.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                    </div>

                    <div class="automation-flow">
                        <div class="flow-box trigger">
                            <div class="flow-icon">{getTriggerInfo(automation.trigger_type).icon}</div>
                            <div class="flow-label">Cuando</div>
                            <p class="flow-title">{getTriggerInfo(automation.trigger_type).label}</p>
                        </div>

                        <div class="flow-arrow">→</div>

                        <div class="flow-box action">
                            <div class="flow-icon">{getActionInfo(automation.action_type).icon}</div>
                            <div class="flow-label">Entonces</div>
                            <p class="flow-title">{getActionInfo(automation.action_type).label}</p>
                        </div>
                    </div>

                    <div class="automation-actions">
                        <button
                            class="btn btn-toggle"
                            class:active={automation.is_active}
                            class:inactive={!automation.is_active}
                            on:click={() => toggleActive(automation)}
                        >
                            {automation.is_active ? '✓ Activa' : '○ Inactiva'}
                        </button>
                        <button class="btn btn-edit" on:click={() => openEditModal(automation)}>
                            ✏️ Editar
                        </button>
                        <button class="btn btn-danger" on:click={() => deleteAutomation(automation.id)}>
                            🗑️ Eliminar
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>{editingAutomation ? 'Editar Automatización' : 'Nueva Automatización'}</h2>

            <form on:submit|preventDefault={saveAutomation}>
                <!-- Basic Info -->
                <div class="form-group">
                    <label for="name">Nombre de la Automatización *</label>
                    <input
                        id="name"
                        type="text"
                        bind:value={formData.name}
                        placeholder="Ej: Activar chatbot en nuevas conversaciones"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="description">Descripción</label>
                    <textarea
                        id="description"
                        bind:value={formData.description}
                        placeholder="Describe qué hace esta automatización..."
                    ></textarea>
                </div>

                <!-- Trigger Section -->
                <div class="form-section">
                    <h3>⚡ Disparador (Cuando...)</h3>

                    <div class="type-selector">
                        {#each triggerTypes as trigger}
                            <div
                                class="type-option"
                                class:selected={formData.trigger_type === trigger.value}
                                on:click={() => formData.trigger_type = trigger.value}
                            >
                                <div class="type-option-icon">{trigger.icon}</div>
                                <div class="type-option-label">{trigger.label}</div>
                                <div class="type-option-desc">{trigger.description}</div>
                            </div>
                        {/each}
                    </div>

                    <!-- Trigger-specific config -->
                    {#if getCurrentTrigger()}
                        {#if getCurrentTrigger().configFields.includes('keywords')}
                            <div class="form-group">
                                <label for="keywords">Palabras Clave</label>
                                <input
                                    id="keywords"
                                    type="text"
                                    bind:value={triggerConfigState.keywords}
                                    placeholder="ayuda, soporte, problema"
                                />
                                <small>Separa las palabras clave con comas.</small>
                            </div>

                            <div class="form-group">
                                <label for="match_type">Tipo de Coincidencia</label>
                                <select id="match_type" bind:value={triggerConfigState.match_type}>
                                    <option value="contains">Contiene</option>
                                    <option value="exact">Exacta</option>
                                    <option value="regex">Expresión Regular</option>
                                </select>
                            </div>
                        {/if}

                        {#if getCurrentTrigger().configFields.includes('direction')}
                            <div class="form-group">
                                <label for="direction">Dirección del Mensaje</label>
                                <select id="direction" bind:value={triggerConfigState.direction}>
                                    <option value="incoming">Entrantes</option>
                                    <option value="outgoing">Salientes</option>
                                    <option value="both">Ambos</option>
                                </select>
                            </div>
                        {/if}

                        {#if getCurrentTrigger().configFields.includes('schedule')}
                            <div class="form-group">
                                <label for="schedule">Programación (Cron Expression)</label>
                                <input
                                    id="schedule"
                                    type="text"
                                    bind:value={triggerConfigState.schedule}
                                    placeholder="0 9 * * * (Diario a las 9:00 AM)"
                                />
                                <small>Usa formato cron. Ejemplo: 0 9 * * * para diario a las 9 AM</small>
                            </div>
                        {/if}
                    {/if}
                </div>

                <!-- Action Section -->
                <div class="form-section">
                    <h3>🎯 Acción (Entonces...)</h3>

                    <div class="type-selector">
                        {#each actionTypes as action}
                            <div
                                class="type-option"
                                class:selected={formData.action_type === action.value}
                                on:click={() => formData.action_type = action.value}
                            >
                                <div class="type-option-icon">{action.icon}</div>
                                <div class="type-option-label">{action.label}</div>
                                <div class="type-option-desc">{action.description}</div>
                            </div>
                        {/each}
                    </div>

                    <!-- Action-specific config -->
                    {#if getCurrentAction()}
                        {#if getCurrentAction().configFields.includes('message_template')}
                            <div class="form-group">
                                <label for="message_template">Plantilla de Mensaje</label>
                                <textarea
                                    id="message_template"
                                    bind:value={actionConfigState.message_template}
                                    placeholder="Hola, gracias por contactarnos..."
                                ></textarea>
                            </div>
                        {/if}

                        {#if getCurrentAction().configFields.includes('chatbot_id')}
                            <div class="form-group">
                                <label for="chatbot_id">Seleccionar Chatbot</label>
                                <select id="chatbot_id" bind:value={actionConfigState.chatbot_id}>
                                    <option value="">Seleccionar...</option>
                                    {#each chatbots.filter(c => c.is_active) as chatbot}
                                        <option value={chatbot.id}>{chatbot.name}</option>
                                    {/each}
                                </select>
                            </div>
                        {/if}

                        {#if getCurrentAction().configFields.includes('assignment_logic')}
                            <div class="form-group">
                                <label for="assignment_logic">Lógica de Asignación</label>
                                <select id="assignment_logic" bind:value={actionConfigState.assignment_logic}>
                                    <option value="round_robin">Round Robin</option>
                                    <option value="least_active">Menos Activo</option>
                                    <option value="specific">Agente Específico</option>
                                </select>
                            </div>
                        {/if}

                        {#if getCurrentAction().configFields.includes('email_to')}
                            <div class="form-group">
                                <label for="email_to">Email Destino</label>
                                <input
                                    id="email_to"
                                    type="email"
                                    bind:value={actionConfigState.email_to}
                                    placeholder="admin@ejemplo.com"
                                />
                            </div>

                            <div class="form-group">
                                <label for="email_subject">Asunto</label>
                                <input
                                    id="email_subject"
                                    type="text"
                                    bind:value={actionConfigState.email_subject}
                                    placeholder="Nueva conversación"
                                />
                            </div>

                            <div class="form-group">
                                <label for="email_template">Plantilla de Email</label>
                                <textarea
                                    id="email_template"
                                    bind:value={actionConfigState.email_template}
                                    placeholder="Se ha recibido una nueva conversación..."
                                ></textarea>
                            </div>
                        {/if}
                    {/if}
                </div>

                <div class="checkbox-group">
                    <input
                        id="is_active"
                        type="checkbox"
                        bind:checked={formData.is_active}
                    />
                    <label for="is_active">Automatización activa</label>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading}>
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
